
import { SidebarProvider } from "@/components/ui/sidebar";
import Map, { Marker, NavigationControl, Popup } from 'react-map-gl';
import { AppSidebar } from "@/components/AppSidebar";
import { useSimpleRole } from "@/hooks/use-simple-role";
import { useRoleFlags } from "@/hooks/use-role-flags";
import { useAuth } from "@/contexts/AuthContext";
import { RefreshCw, Layers, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useIsMobile } from "@/hooks/use-mobile";
import { DeviceMapMarker } from "@/components/devices/DeviceMapMarker";
import { DeviceDetails } from "@/components/devices/DeviceDetails";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { PAGE_HEADERS } from "@/constants/page-headers";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface DeviceAssignment {
  id: string;
  device_id: string;
  user_id: string;
  device_details?: {
    device_id: string;
    device_name: string | null;
    device_description: string | null;
    lat_wgs84: number | null;
    long_wgs84: number | null;
    location?: {
      latWGS84?: number | string | null;
      longWGS84?: number | string | null;
      lat_wgs84?: number | string | null;
      long_wgs84?: number | string | null;
    } | null;
    gnss?: {
      latitude?: number | string | null;
      longitude?: number | string | null;
      northSouth?: string | null;
      eastWest?: string | null;
    } | null;
    imagekit?: {
      thumbnailUrl?: string;
      url?: string;
    } | null;
    device_last_updated: string | null;
    device_status: string | null;
    device_firmware: string | null;
    device_serial: string | null;
  } | null;
}

const parseCoordinate = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const coordinate = typeof value === "number" ? value : Number(value);
  return Number.isFinite(coordinate) ? coordinate : null;
};

// Converts NMEA ddmm.mmmm / dddmm.mmmm to signed decimal degrees (same rule as the Location tab).
const nmeaToDecimal = (value: unknown, direction: unknown, negative: string): number | null => {
  const raw = parseCoordinate(value);
  if (raw === null || raw === 0) return null;
  const abs = Math.abs(raw);
  const degrees = Math.floor(abs / 100);
  const decimal = degrees + (abs - degrees * 100) / 60;
  const isNegative = typeof direction === "string" && direction.trim().toUpperCase() === negative;
  return isNegative ? -decimal : decimal;
};

const getDeviceCoordinates = (device?: DeviceAssignment["device_details"] | null) => {
  const gnssLat = nmeaToDecimal(device?.gnss?.latitude, device?.gnss?.northSouth, "S");
  const gnssLong = nmeaToDecimal(device?.gnss?.longitude, device?.gnss?.eastWest, "W");
  if (gnssLat !== null && gnssLong !== null && Math.abs(gnssLat) <= 90 && Math.abs(gnssLong) <= 180) {
    return { latitude: gnssLat, longitude: gnssLong };
  }
  const latitude = parseCoordinate(device?.lat_wgs84 ?? device?.location?.latWGS84 ?? device?.location?.lat_wgs84);
  const longitude = parseCoordinate(device?.long_wgs84 ?? device?.location?.longWGS84 ?? device?.location?.long_wgs84);

  return { latitude, longitude };
};

const hasValidDeviceCoordinates = (device?: DeviceAssignment["device_details"] | null) => {
  const { latitude, longitude } = getDeviceCoordinates(device);
  return (
    latitude !== null &&
    longitude !== null &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180
  );
};

const MapPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const { session } = useAuth();
  const { role, isSystemAdmin } = useSimpleRole();
  const { isDemoOnly, hasSuperuserSurface } = useRoleFlags();
  const showAllDevicesFleet = isSystemAdmin || hasSuperuserSurface || isDemoOnly;
  const [selectedDevice, setSelectedDevice] = useState<DeviceAssignment | null>(null);
  const [mapboxToken, setMapboxToken] = useState<string | null>(null);
  const [mapStyle, setMapStyle] = useState('mapbox://styles/mapbox/streets-v12');
  const [lastRealtimeUpdate, setLastRealtimeUpdate] = useState<Date | null>(null);
  const [selectedDeviceForSheet, setSelectedDeviceForSheet] = useState<string | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [mapRef, setMapRef] = useState<any>(null);

  const mapStyles = [
    { name: 'Streets', value: 'mapbox://styles/mapbox/streets-v12' },
    { name: 'Satellite', value: 'mapbox://styles/mapbox/satellite-v9' },
    { name: 'Satellite Streets', value: 'mapbox://styles/mapbox/satellite-streets-v12' },
    { name: 'Light', value: 'mapbox://styles/mapbox/light-v11' },
    { name: 'Dark', value: 'mapbox://styles/mapbox/dark-v11' },
    { name: 'Navigation Day', value: 'mapbox://styles/mapbox/navigation-day-v1' },
    { name: 'Navigation Night', value: 'mapbox://styles/mapbox/navigation-night-v1' },
    { name: 'Outdoors', value: 'mapbox://styles/mapbox/outdoors-v12' }
  ];

  const [tokenError, setTokenError] = useState(false);
  const fetchMapboxToken = useCallback(async () => {
    setTokenError(false);
    try {
      const { data, error } = await supabase.functions.invoke('getMapboxToken');
      if (error) throw error;
      if (!data?.token) throw new Error('No Mapbox token returned');
      setMapboxToken(data.token);
    } catch (error) {
      console.error('Error fetching Mapbox token:', error);
      setTokenError(true);
    }
  }, []);

  useEffect(() => {
    fetchMapboxToken();
  }, [fetchMapboxToken]);

  // Set up realtime subscription for device location updates
  useEffect(() => {
    console.log('Setting up realtime subscription for device locations on map');
    
    const channel = supabase
      .channel(`map-device-locations-${Date.now()}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'device_details'
      }, (payload) => {
        console.log('Realtime location update received on map:', payload);
        setLastRealtimeUpdate(new Date());
        
        // Invalidate the assignments query to refetch data
        queryClient.invalidateQueries({ queryKey: ["device-assignments-with-names", isSystemAdmin, role] });
      })
      .subscribe((status) => {
        console.log('Map device locations subscription status:', status);
      });

    return () => {
      console.log('Cleaning up map device locations subscription');
      channel.unsubscribe().then(() => {
        supabase.removeChannel(channel);
      });
    };
  }, [queryClient]);

  const { data: assignments, isLoading, error } = useQuery({
    queryKey: ["device-assignments-with-names", isSystemAdmin, role, isDemoOnly, hasSuperuserSurface],
    queryFn: async () => {
      console.log('🗺️ [Map] Starting device query');
      console.log('🗺️ [Map] User role:', role, 'isSystemAdmin:', isSystemAdmin);
      
      const { data: session } = await supabase.auth.getSession();
      
      if (!session?.session) {
        console.log('🗺️ [Map] No session found, redirecting to login');
        navigate("/");
        throw new Error("No session");
      }

      if (showAllDevicesFleet) {
        // Superuser / internal_staff / advisor: Get ALL devices then demo-filter
        const { data, error } = await supabase
          .from('device_details')
          .select(`
            device_id,
            device_name,
            device_description,
            device_last_updated,
            device_status,
            device_firmware,
            device_serial,
            lat_wgs84,
            long_wgs84,
            location,
            gnss,
            imagekit
          `);
        
        if (error) {
          console.error("🗺️ [Map] Error fetching all device details:", error);
          throw error;
        }

        const filtered = isDemoOnly
          ? (data || []).filter(d => d.device_status?.toLowerCase() === 'demo')
          : (data || []);

        // Convert to assignment format for compatibility
        const allDevicesData = filtered.map(deviceDetails => ({
          id: deviceDetails.device_id,
          device_id: deviceDetails.device_id,
          user_id: 'system', // Special marker for system-wide view
          assigned_at: new Date().toISOString(), // Current timestamp
          device_details: deviceDetails
        }));
        
        console.log("🗺️ [Map] All devices formatted:", allDevicesData.length);
        return allDevicesData as DeviceAssignment[];
        
      } else {
        // Regular user: Get only assigned devices
        console.log('🗺️ [Map] Regular user detected - fetching assigned devices only');
        console.log('🗺️ [Map] Querying device assignments for user:', session.session.user.id);

        // First get device assignments for the user
        const { data: assignmentData, error: assignmentError } = await supabase
          .from('device_assignments')
          .select('device_id, assigned_at')
          .eq('user_id', session.session.user.id);
        
        if (assignmentError) {
          console.error("🗺️ [Map] Error fetching assignments:", assignmentError);
          throw assignmentError;
        }

        if (!assignmentData || assignmentData.length === 0) {
          console.log("🗺️ [Map] No device assignments found for user");
          return [];
        }

        const deviceIds = assignmentData.map(a => a.device_id);
        console.log("🗺️ [Map] Found device IDs:", deviceIds);

        // Then get device details for those device IDs
        const { data, error } = await supabase
          .from('device_details')
          .select(`
            device_id,
            device_name,
            device_description,
            device_last_updated,
            device_status,
            device_firmware,
            device_serial,
            lat_wgs84,
            long_wgs84,
            location,
            gnss,
            imagekit
          `)
          .in('device_id', deviceIds);
        
        if (error) {
          console.error("🗺️ [Map] Error fetching device details:", error);
          throw error;
        }

        console.log("🗺️ [Map] Raw device details data (assigned):", data);

        // Combine assignment data with device details
        const combinedData = assignmentData.map(assignment => {
          const deviceDetails = data?.find(device => device.device_id === assignment.device_id);
          return {
            id: assignment.device_id, // Use device_id as id for compatibility
            device_id: assignment.device_id,
            user_id: session.session.user.id,
            assigned_at: assignment.assigned_at,
            device_details: deviceDetails
          };
        }).filter(item => item.device_details); // Only include items where we found device details
        
        console.log("🗺️ [Map] Combined data:", combinedData);
        console.log("🗺️ [Map] Number of combined assignments:", combinedData.length);
        
        return combinedData as DeviceAssignment[];
      }
    },
    enabled: !!session && role !== undefined && isSystemAdmin !== undefined, // Only run when session and role are stable
    staleTime: Infinity, // Never consider data stale - only refresh manually
    refetchInterval: false, // No automatic polling
    refetchOnWindowFocus: false, // No refresh on window focus
    refetchOnMount: false, // Don't refetch on component remount
    retry: 1,
  });

  const devicesWithLocation = assignments?.filter(assignment => {
    console.log('🗺️ [Map] Filtering device:', assignment.device_id);
    
    const { latitude, longitude } = getDeviceCoordinates(assignment.device_details);
    
    console.log('🗺️ [Map] Device location raw data:', {
      deviceId: assignment.device_id,
      lat: latitude,
      long: longitude,
      device_details: assignment.device_details
    });
    
    const isValidCoordinates = hasValidDeviceCoordinates(assignment.device_details);
    
    console.log("🗺️ [Map] Device validation:", {
      deviceId: assignment.device_id,
      lat: latitude,
      long: longitude,
      willBeIncluded: isValidCoordinates
    });
    
    return isValidCoordinates;
  }) || [];

  console.log('🗺️ [Map] Filtered devices with valid locations:', devicesWithLocation.length);

  const initialViewState = devicesWithLocation.length > 0
    ? {
        latitude: devicesWithLocation.reduce((sum, device) => 
          sum + (getDeviceCoordinates(device.device_details).latitude || 0), 0) / devicesWithLocation.length,
        longitude: devicesWithLocation.reduce((sum, device) => 
          sum + (getDeviceCoordinates(device.device_details).longitude || 0), 0) / devicesWithLocation.length,
        zoom: 8
      }
    : {
        latitude: 51.5074,
        longitude: -0.1278,
        zoom: 8
      };

  const handleRefresh = () => {
    console.log('Manual refresh triggered for map');
    queryClient.invalidateQueries({ queryKey: ["device-assignments-with-names", isSystemAdmin, role] });
    toast.success("Refreshing device locations...");
  };

  const handleDeviceClick = (deviceId: string) => {
    setSelectedDeviceForSheet(deviceId);
    setIsSheetOpen(true);
  };

  const handleTableRowClick = (assignment: DeviceAssignment) => {
    const device = assignment.device_details;
    const { latitude, longitude } = getDeviceCoordinates(device);
    const willShowOnMap = hasValidDeviceCoordinates(device);

    if (willShowOnMap && mapRef && latitude !== null && longitude !== null) {
      // Center and zoom the map to this device location
      mapRef.flyTo({
        center: [longitude, latitude],
        zoom: 15,
        duration: 1500
      });
    }
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        <main className="flex-1">
          <FixedPageHeader
            {...PAGE_HEADERS.map}
            actions={
              <div className="flex gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2 text-xs bg-[#DFEF97] text-black hover:bg-[#DFEF97]/90"
                    >
                      <Layers className="h-3 w-3" />
                      Map Style
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-[200px]">
                    {mapStyles.map((style) => (
                      <DropdownMenuItem
                        key={style.value}
                        className="cursor-pointer"
                        onClick={() => setMapStyle(style.value)}
                      >
                        {style.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                  <Button
                    onClick={handleRefresh}
                    variant="outline"
                    size="sm"
                    className="gap-2 text-xs"
                  >
                    <RefreshCw className="h-3 w-3" />
                    Refresh
                  </Button>
                </div>
              }
            />
            <div className="px-4 md:px-8 py-6 md:py-8 space-y-8 pt-24 md:pt-[8.5rem]">
              {/* Status indicator for realtime updates */}
              {lastRealtimeUpdate && (
                <div className="text-xs text-muted-foreground">
                  Last location update: {lastRealtimeUpdate.toLocaleTimeString()}
                </div>
              )}
              
              {error && (
                <div className="text-sm text-red-500">
                  Error loading devices. Please try refreshing the page.
                </div>
              )}
              {isLoading && <div className="text-sm">Loading devices...</div>}
              
            
            <div className="w-full h-[calc(100vh-9rem)] md:h-[calc(100vh-16rem)] rounded-lg overflow-hidden border relative m-2 md:m-0">
              {mapboxToken ? (
                <Map
                  ref={(ref) => setMapRef(ref?.getMap())}
                  mapboxAccessToken={mapboxToken}
                  initialViewState={initialViewState}
                  style={{ width: "100%", height: "100%" }}
                  mapStyle={mapStyle}
                  reuseMaps
                >
                  <NavigationControl position="top-right" />
                  
                  {devicesWithLocation.map((device) => {
                    const { latitude, longitude } = getDeviceCoordinates(device.device_details);
                    const thumbnailUrl = device.device_details?.imagekit?.thumbnailUrl;
                    
                    if (latitude === null || longitude === null) return null;
                    
                    return (
                      <Marker
                        key={device.device_id}
                        latitude={latitude}
                        longitude={longitude}
                        onClick={(e) => {
                          e.originalEvent.stopPropagation();
                          setSelectedDevice(device);
                        }}
                      >
                        <div className="cursor-pointer w-12 h-12 rounded-full overflow-hidden border-2 border-white shadow-lg hover:scale-110 transition-transform">
                          {thumbnailUrl ? (
                            <img
                              src={thumbnailUrl}
                              alt={device.device_details?.device_name || "Device"}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-brand-500 flex items-center justify-center">
                              <Camera className="h-6 w-6 text-white" />
                            </div>
                          )}
                        </div>
                      </Marker>
                    );
                  })}

                  {selectedDevice && (() => {
                    const { latitude, longitude } = getDeviceCoordinates(selectedDevice.device_details);
                    if (latitude === null || longitude === null) return null;

                    return (
                      <Popup
                        latitude={latitude}
                        longitude={longitude}
                        closeOnClick={false}
                        onClose={() => setSelectedDevice(null)}
                        offset={[0, -10]}
                      >
                        <DeviceMapMarker
                          id={selectedDevice.device_id}
                          name={selectedDevice.device_details?.device_name || selectedDevice.device_id}
                          description={selectedDevice.device_details?.device_description || undefined}
                          thumbnailUrl={selectedDevice.device_details?.imagekit?.thumbnailUrl}
                          onDeviceClick={handleDeviceClick}
                        />
                      </Popup>
                    );
                  })()}
                </Map>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-3">
                  {tokenError ? (
                    <>
                      <p className="text-sm text-muted-foreground">Couldn't load the map.</p>
                      <Button variant="outline" size="sm" onClick={fetchMapboxToken}>Retry loading map</Button>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">Loading map...</p>
                  )}
                </div>
              )}
            </div>

            {/* Debug table showing device data - Hidden on mobile */}
            <Card className="hidden md:block mt-8">
              <CardHeader>
                <CardTitle className="text-lg">Device List</CardTitle>
                <p className="text-sm text-muted-foreground">
                  {isSystemAdmin 
                    ? "Showing ALL devices in the system (Superuser view)" 
                    : "Showing only devices assigned to your account"}
                  {role && ` | Your role: ${role}`}
                </p>
              </CardHeader>
              <CardContent>
                {assignments && assignments.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-center">Device ID</TableHead>
                        <TableHead className="text-center">Device Name</TableHead>
                        <TableHead className="text-center">Latitude</TableHead>
                        <TableHead className="text-center">Longitude</TableHead>
                        <TableHead className="text-center">Last Updated</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {assignments.map((assignment) => {
                        const device = assignment.device_details;
                        const { latitude, longitude } = getDeviceCoordinates(device);
                        const lastUpdated = device?.device_last_updated;
                        const willShowOnMap = hasValidDeviceCoordinates(device);

                        return (
                           <TableRow 
                            key={assignment.device_id}
                            onClick={() => handleTableRowClick(assignment)}
                            className={willShowOnMap ? "cursor-pointer hover:bg-muted/50" : ""}
                          >
                            <TableCell className="font-mono text-xs">
                              {assignment.device_id}
                            </TableCell>
                            <TableCell>
                              {device?.device_name || "No name"}
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {latitude !== null ? latitude.toString() : "null"}
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {longitude !== null ? longitude.toString() : "null"}
                            </TableCell>
                            <TableCell className="text-xs">
                              {lastUpdated ? new Date(lastUpdated).toLocaleDateString() : "Never"}
                            </TableCell>
                            <TableCell>
                              <span 
                                className={`text-xs px-2 py-1 rounded ${
                                  willShowOnMap 
                                    ? "bg-green-100 text-green-800" 
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {willShowOnMap ? "On Map" : "Hidden"}
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    {isLoading ? "Loading device data..." : "No device assignments found"}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Device Details Sheet/Dialog - Responsive */}
            {isMobile ? (
              <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
                <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle>Device Details</SheetTitle>
                  </SheetHeader>
                  {selectedDeviceForSheet && (
                    <div className="mt-6">
                      <DeviceDetails deviceId={selectedDeviceForSheet} />
                    </div>
                  )}
                </SheetContent>
              </Sheet>
            ) : (
              <Dialog open={isSheetOpen} onOpenChange={setIsSheetOpen}>
                <DialogContent className="w-[90vw] max-w-[80vw] h-[80vh] max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Device Details</DialogTitle>
                  </DialogHeader>
                  {selectedDeviceForSheet && (
                    <div className="mt-6">
                      <DeviceDetails deviceId={selectedDeviceForSheet} />
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            )}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default MapPage;
